'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { getOnHoldCryptoDeposits } from '../gqlActions';
import {
  SelfDeclarationDepositDrawer,
  SelfDeclarationDepositDrawerPropsWithTransactionId,
} from '../../crypto-transfers/components/SelfDeclarationDepositDrawer/SelfDeclarationDepositDrawer';
import { useAppNotification } from '../../notifications/NotificationContextProvider';
import { useModalDrawerContext } from '../components/ModalDrawerContext/ModalDrawerContext';

// ? The scope of this component is to incapsulate business logic related
// ? to displaying notifications that require mandatory immediate action.
export const ActionRequiredNotificationsObserver = () => {
  const { setAppNotification, closeNotificationById } = useAppNotification();

  const pathname = usePathname();

  const { t } = useClientTranslation();

  const { openDrawer } = useModalDrawerContext();

  const checkIfWalletOwnershipVerificationIsNeeded = async () => {
    const onHoldDeposits = await getOnHoldCryptoDeposits();

    if (onHoldDeposits?.length > 0) {
      setAppNotification({
        id: 'wallet_ownership_verification_required',
        title: t('tx_details_on_hold_status_title'),
        subtitle: t('tx_details_on_hold_description'),
        withClose: true,
        visual: 'warning',
        type: 'global',
        customDuration: 'infinite',
        action: {
          altText: t('cd_on_hold_verify_now'),
          component: (
            <a
              role="button"
              {...(onHoldDeposits.length > 1
                ? { href: NEXT_ROUTES.TRANSACTIONS }
                : {})}
              {...(onHoldDeposits.length === 1
                ? {
                    onClick: () =>
                      openDrawer(SelfDeclarationDepositDrawer, {
                        transactionId:
                          onHoldDeposits[0]?.node?.content?.transferId ||
                          onHoldDeposits[0]?.node?.id,
                        notificationId: onHoldDeposits[0]?.node?.id,
                      } as SelfDeclarationDepositDrawerPropsWithTransactionId),
                  }
                : {})}
              className={css({
                fontSize: 'body.medium_medium',
                fontWeight: 'body.medium_medium',
                letterSpacing: 'body.medium_medium',
                lineHeight: 'body.medium_medium',
                textDecoration: 'underline',
                cursor: 'pointer',
              })}
            >
              {t('cd_on_hold_verify_now')}
            </a>
          ),
        },
      });
    }
  };

  useEffect(() => {
    if (pathname === NEXT_ROUTES.PORTFOLIO) {
      checkIfWalletOwnershipVerificationIsNeeded();
    }

    return () => {
      closeNotificationById('wallet_ownership_verification_required');
    };
  }, []);

  return null;
};
